import { Ref, useEffect, useState } from "react";
import type {
  RequestTransformFunction,
  StyleSpecification,
} from "maplibre-gl";
import Map, {
  LngLatBoundsLike,
  MapLayerMouseEvent,
  MapRef,
} from "react-map-gl/maplibre";
import { MAP_STYLES } from "@/components/basemap/styles";
import { useTheme } from "@/components/theme-provider";
import { useSidebar } from "@/components/ui/sidebar";

interface BasemapProps {
  longitude?: number;
  latitude?: number;
  bbox?: LngLatBoundsLike;
  bboxPadding?: number;
  zoom?: number;
  pitch?: number;
  bearing?: number;
  onZoom: (zoom: number) => void;
  style: React.CSSProperties;
  children?: React.ReactNode;
  transformRequest?: RequestTransformFunction;
  mapRef: Ref<MapRef>;
  onClick?: (event: MapLayerMouseEvent) => void;
  interactiveLayerIds?: string[];
  onLoad?: () => void;
  attributionControl: boolean;
  minZoom?: number;
  maxZoom?: number;
  interactive?: boolean;
  mapStyle?: string;
  boundingBox?: LngLatBoundsLike;
  basemap?: string;
}

/**
 * Examples of using native MapLibre layers: https://visgl.github.io/react-map-gl/docs/get-started/adding-custom-data#native-maplibre-layers
 * Examples of using Custom Overlays: https://visgl.github.io/react-map-gl/docs/get-started/adding-custom-data#custom-overlays
 * Other Examples: https://visgl.github.io/react-map-gl/examples
 * @param props
 * @returns
 */
export const DEFAULT_MAP_VIEW = {
  latitude: -35.32186,
  longitude: 149.14683,
  zoom: 7,
} as const;

export const Basemap = (props: BasemapProps) => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  const getMapStyle = (): string | StyleSpecification => {
    if (props.basemap === "Imagery") {
      return MAP_STYLES.IMAGERY;
    }

    if (props.mapStyle === "BLANK") {
      return isDark ? MAP_STYLES.BLANK_DARK : MAP_STYLES.BLANK_LIGHT;
    }

    if (props.mapStyle) {
      return props.mapStyle;
    }

    return isDark ? MAP_STYLES.DEFAULT_DARK : MAP_STYLES.DEFAULT_LIGHT;
  };

  // If lat/lon/zoom are passed it will initialise the map to there
  const [viewState, setViewState] = useState(
    props.latitude && props.longitude && props.zoom
      ? {
          longitude: props.longitude,
          latitude: props.latitude,
          zoom: props.zoom,
          ...(props.pitch !== undefined && { pitch: props.pitch }),
          ...(props.bearing !== undefined && { bearing: props.bearing }),
        }
      : {}
  );

  // Otherwise if bbox is passed we will initialise to the bbox
  const initialViewState = props.bbox
    ? {
        bounds: props.bbox,
        fitBoundsOptions: { padding: props.bboxPadding ?? 20 },
      }
    : undefined;

  // When the sidebar opens/closes, repeatedly resize the map during the
  // CSS transition so the canvas tracks the container width.
  const { state: sidebarState } = useSidebar();
  useEffect(() => {
    const mapRef = props.mapRef as { current: MapRef | null } | null;
    const map = mapRef?.current?.getMap();
    if (!map) return;
    const start = performance.now();
    const TRANSITION_MS = 250;
    let rafId: number;
    const tick = (now: number) => {
      map.resize();
      if (now - start < TRANSITION_MS) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [sidebarState, props.mapRef]);

  // Panel drags don't fire a window resize, so watch the container directly.
  // Gated on `mapLoaded` - react-map-gl publishes the ref only after creating the map.
  const [mapLoaded, setMapLoaded] = useState(false);
  useEffect(() => {
    if (!mapLoaded) return;
    const mapRef = props.mapRef as { current: MapRef | null } | null;
    const map = mapRef?.current?.getMap();
    if (!map) return;
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(() => map.resize());
    });
    observer.observe(map.getContainer());
    return () => observer.disconnect();
  }, [mapLoaded, props.mapRef]);

  return (
    <Map
      {...viewState}
      initialViewState={initialViewState}
      interactive={props.interactive ?? true}
      interactiveLayerIds={props.interactiveLayerIds}
      onMove={(evt) => setViewState(evt.viewState)}
      onZoom={(e) => props.onZoom(e.viewState.zoom)}
      onLoad={() => {
        setMapLoaded(true);
        props.onLoad?.();
      }}
      ref={props.mapRef}
      onClick={props.onClick}
      transformRequest={props.transformRequest}
      style={props.style}
      styleDiffing
      mapStyle={getMapStyle()}
      attributionControl={props.attributionControl ? {} : false}
      minZoom={props.minZoom ? props.minZoom : 0}
      maxZoom={props.maxZoom ? props.maxZoom : 24}
      // react-map-gl types maxBounds as a tuple, but maplibre accepts any
      // LngLatBoundsLike at runtime.
      maxBounds={props.boundingBox as [number, number, number, number]}
    >
      {props.children}
    </Map>
  );
};
